import { useState } from "react";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import SeoMeta from "../../components/base/SeoMeta";

const BASE_URL = (import.meta.env.VITE_SITE_URL as string) || "https://flightfare.pk";

const ABOUT_JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About flightfare.pk – Pakistan's Trusted Travel Agency",
    "url": `${BASE_URL}/about`,
    "description": "Learn about flightfare.pk, Pakistan's trusted travel agency since 2014 offering flights, tours, Umrah packages, visa assistance and travel insurance from Lahore.",
    "mainEntity": {
      "@type": "TravelAgency",
      "name": "flightfare.pk",
      "alternateName": "Quick Fly Travel",
      "foundingDate": "2014",
      "numberOfEmployees": { "@type": "QuantitativeValue", "value": 20 },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "GF & BM 9A, AA Commercial, Street 1, Sector D",
        "addressLocality": "Bahria Town, Lahore",
        "addressRegion": "Punjab",
        "addressCountry": "PK"
      },
      "telephone": "+923711432451",
      "url": BASE_URL,
      "sameAs": [
        "https://www.facebook.com/share/1E1YkhMex1/",
        "http://instagram.com/flightfare.pk/"
      ]
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "flightfare.pk",
    "alternateName": "Quick Fly Travel",
    "url": BASE_URL,
    "foundingDate": "2014",
    "foundingLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Karachi",
        "addressCountry": "PK"
      }
    },
    "description": "Pakistan's trusted travel agency for cheap flights, international tours, Umrah packages, visa assistance and travel insurance. Serving 25,000+ travelers since 2014.",
    "award": "IATA Accredited Travel Agency",
    "memberOf": {
      "@type": "Organization",
      "name": "Pakistan Tourism Authority"
    }
  }
];

const STATS = [
  { label: "Happy Travelers", value: "25,000+" },
  { label: "Years of Experience", value: "10+" },
  { label: "Tour Destinations", value: "50+" },
  { label: "Trusted Partners", value: "120+" },
];

const VALUES = [
  {
    icon: "ri-shield-check-line",
    title: "Trusted & Transparent",
    desc: "No hidden fees, no last-minute surprises. What we quote is what you pay — every time.",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "24/7 Support",
    desc: "Our team is reachable around the clock — before departure, during travel, and after you return.",
  },
  {
    icon: "ri-map-2-line",
    title: "Expert Itineraries",
    desc: "Hand-crafted by travel experts who have personally explored every destination we sell.",
  },
  {
    icon: "ri-wallet-3-line",
    title: "Best Value Pricing",
    desc: "We leverage long-term airline and hotel partnerships to pass real savings onto our customers.",
  },
  {
    icon: "ri-group-line",
    title: "Family-Friendly",
    desc: "All packages are designed with families in mind — safe, comfortable, and memorable experiences.",
  },
  {
    icon: "ri-award-line",
    title: "IATA Accredited",
    desc: "Fully licensed travel agency accredited by IATA and registered with Pakistan Tourism Authority.",
  },
];

const SERVICES_OFFERED = [
  { icon: "ri-flight-takeoff-line", label: "International Flights" },
  { icon: "ri-hotel-line", label: "Hotel Bookings" },
  { icon: "ri-earth-line", label: "Tour Packages" },
  { icon: "ri-passport-line", label: "Visa Assistance" },
  { icon: "ri-map-pin-line", label: "Umrah Packages" },
  { icon: "ri-shield-flash-line", label: "Travel Insurance" },
  { icon: "ri-plane-line", label: "Airline Ticketing" },
  { icon: "ri-graduation-cap-line", label: "Travel Consulting" },
];

const MILESTONES = [
  { year: "2014", title: "Company Founded", desc: "Started as a small ticketing office in Karachi with a big dream." },
  { year: "2016", title: "First Tour Package", desc: "Launched our first group tour to Malaysia & Thailand — fully sold out." },
  { year: "2018", title: "Umrah Division Opened", desc: "Dedicated Umrah operation launched to serve pilgrims across Pakistan." },
  { year: "2020", title: "Digital Transformation", desc: "Launched online bookings, reaching customers in every city nationwide." },
  { year: "2022", title: "25,000 Happy Travelers", desc: "Crossed a major milestone — 25,000 customers served with pride." },
  { year: "2024", title: "flightfare.pk Launched", desc: "Rebranded and launched our digital platform to serve Pakistan even better." },
];

export default function AboutPage() {
  const [activeMilestone, setActiveMilestone] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <SeoMeta
        title="About flightfare.pk – Trusted Travel Agency Pakistan Since 2014"
        description="Discover the story of flightfare.pk, Pakistan's IATA-accredited travel agency. 10+ years serving 25,000+ travelers with flights, tours, Umrah, and visa services from Lahore."
        keywords="about flightfare.pk, Pakistan travel agency, IATA accredited travel agency Lahore, trusted travel agency Pakistan, Quick Fly Travel"
        canonical="/about"
        jsonLd={ABOUT_JSON_LD}
      />

      {/* ── Hero ── */}
      <div className="pt-16">
        <div className="relative w-full h-72 md:h-[420px] overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/290f1143d162ef9e63dff81afa2a7754.jpg"
            alt="About flightfare.pk"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-amber-400" />
          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-12 pb-10">
            <div className="max-w-7xl mx-auto">
              <p className="text-amber-300 text-xs font-bold tracking-widest uppercase mb-2">
                <a href="/" className="hover:underline cursor-pointer">Home</a>
                <span className="mx-2 text-white/40">/</span>
                About Us
              </p>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight">
                About <span className="text-amber-400">flightfare.pk</span>
              </h1>
              <p className="text-white/80 text-sm md:text-base mt-3 max-w-xl leading-relaxed">
                Pakistan&apos;s trusted travel partner — connecting people to the world since 2014. We make flights, tours, Umrah, and visas simple, affordable, and reliable.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats Bar ── */}
      <div className="bg-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-12 py-3 grid grid-cols-2 md:grid-cols-4 gap-2 md:divide-x divide-white/20">
          {STATS.map(stat => (
            <div key={stat.label} className="text-center px-3 py-1">
              <p className="text-white font-extrabold text-xl md:text-2xl">{stat.value}</p>
              <p className="text-cyan-100 text-xs font-medium mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Our Story ── */}
      <section className="max-w-7xl mx-auto px-4 md:px-12 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-cyan-600 text-xs font-bold tracking-widest uppercase mb-2">Our Story</p>
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 leading-tight mb-4">
              A Decade of Helping Pakistanis See the World
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              flightfare.pk was born from a simple belief: every Pakistani deserves to travel the world without stress, overpriced tickets, or confusing paperwork. What started as a small ticketing office in Karachi in 2014 has grown into one of Pakistan&apos;s most trusted full-service travel agencies.
            </p>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              Over the years, we have helped over 25,000 travelers board flights, explore new cultures, perform Umrah with peace of mind, and navigate the complexities of international visa applications — all with the same personal care that defined us from day one.
            </p>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              Today, through <strong className="text-gray-800">flightfare.pk</strong>, we bring the same expertise online — giving every Pakistani, in every city, access to flight deals, curated tours, Umrah packages, and visa assistance from their phone or laptop.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="/tours" className="bg-cyan-600 text-white text-sm font-bold px-5 py-2.5 rounded-md hover:bg-cyan-700 transition-colors cursor-pointer whitespace-nowrap">
                Explore Tours
              </a>
              <a href="/contact" className="border border-gray-300 text-gray-700 text-sm font-bold px-5 py-2.5 rounded-md hover:border-gray-400 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                Contact Us
              </a>
            </div>
          </div>
          <div className="relative">
            <div className="w-full h-80 rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Pakistani%20travel%20agency%20office%20interior%2C%20cheerful%20staff%20helping%20customers%20at%20modern%20reception%20desk%2C%20colorful%20travel%20posters%20on%20walls%2C%20bright%20welcoming%20atmosphere%2C%20professional%20team%20in%20formal%20attire%2C%20wide%20angle%20interior%20photography&width=700&height=500&seq=about-story-img&orientation=landscape"
                alt="Our team at flightfare.pk"
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-amber-400 rounded-xl px-5 py-4 shadow-sm">
              <p className="text-white font-extrabold text-2xl">10+</p>
              <p className="text-white/90 text-xs font-semibold">Years in Business</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mission & Vision ── */}
      <section className="bg-gray-50 py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-8">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-cyan-50 mb-5">
                <i className="ri-focus-3-line text-2xl text-cyan-600"></i>
              </div>
              <h3 className="text-lg font-extrabold text-gray-900 mb-3">Our Mission</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                To make international travel accessible, affordable, and stress-free for every Pakistani — whether they&apos;re chasing adventure, visiting family abroad, performing Umrah, or discovering new cultures. We handle the complexity so our customers can focus on the journey.
              </p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-8">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-amber-50 mb-5">
                <i className="ri-eye-line text-2xl text-amber-500"></i>
              </div>
              <h3 className="text-lg font-extrabold text-gray-900 mb-3">Our Vision</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                To become Pakistan&apos;s most trusted digital travel platform — where every booking is transparent, every experience is exceptional, and every traveler returns home with memories worth keeping. We envision a Pakistan where traveling the world is a reality, not a luxury.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── What We Offer ── */}
      <section className="max-w-7xl mx-auto px-4 md:px-12 py-14">
        <div className="text-center mb-10">
          <p className="text-cyan-600 text-xs font-bold tracking-widest uppercase mb-2">What We Offer</p>
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">Complete Travel Services Under One Roof</h2>
          <p className="text-gray-500 text-sm mt-2 max-w-lg mx-auto">From your first flight search to your final return — we handle it all.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {SERVICES_OFFERED.map(s => (
            <div key={s.label} className="bg-gray-50 rounded-xl p-5 text-center border border-gray-100 hover:border-cyan-200 hover:bg-cyan-50 transition-colors cursor-default">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-gray-200 mx-auto mb-3">
                <i className={`${s.icon} text-xl text-cyan-600`}></i>
              </div>
              <p className="text-sm font-semibold text-gray-800">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Why Choose Us ── */}
      <section className="bg-gray-50 py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="text-center mb-10">
            <p className="text-cyan-600 text-xs font-bold tracking-widest uppercase mb-2">Why Choose Us</p>
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">The flightfare.pk Difference</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUES.map(v => (
              <div key={v.title} className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-cyan-50 mb-4">
                  <i className={`${v.icon} text-2xl text-cyan-600`}></i>
                </div>
                <h4 className="text-sm font-extrabold text-gray-900 mb-2">{v.title}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Our Journey (Timeline) ── */}
      <section className="max-w-7xl mx-auto px-4 md:px-12 py-14">
        <div className="text-center mb-10">
          <p className="text-cyan-600 text-xs font-bold tracking-widest uppercase mb-2">Our Journey</p>
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">A Decade of Milestones</h2>
        </div>
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-px bg-gray-200 hidden md:block" />
          <div className="space-y-6">
            {MILESTONES.map((m, i) => (
              <div
                key={m.year}
                className={`relative flex flex-col md:flex-row items-start md:items-center gap-4 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}
              >
                {/* Card */}
                <div className="flex-1">
                  <button
                    type="button"
                    onClick={() => setActiveMilestone(activeMilestone === i ? null : i)}
                    className={`w-full text-left rounded-xl border p-5 transition-all cursor-pointer ${activeMilestone === i ? "border-cyan-400 bg-cyan-50" : "border-gray-200 bg-white hover:border-cyan-200"}`}
                  >
                    <div className="flex items-center gap-3 mb-1">
                      <span className="bg-amber-400 text-white text-xs font-extrabold px-3 py-1 rounded-full whitespace-nowrap">{m.year}</span>
                      <span className="text-sm font-extrabold text-gray-900">{m.title}</span>
                    </div>
                    {activeMilestone === i && (
                      <p className="text-sm text-gray-600 mt-2 leading-relaxed">{m.desc}</p>
                    )}
                  </button>
                </div>
                {/* Center dot */}
                <div className="hidden md:flex w-4 h-4 rounded-full bg-cyan-600 border-4 border-white flex-shrink-0 z-10 items-center justify-center" style={{ boxShadow: "0 0 0 2px #0891b2" }} />
                {/* Empty side */}
                <div className="flex-1 hidden md:block" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Accreditations ── */}
      <section className="max-w-7xl mx-auto px-4 md:px-12 py-12">
        <div className="text-center mb-8">
          <p className="text-cyan-600 text-xs font-bold tracking-widest uppercase mb-2">Credibility</p>
          <h2 className="text-xl md:text-2xl font-extrabold text-gray-900">Licensed & Accredited</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-3xl mx-auto">
          {[
            { icon: "ri-award-fill", color: "text-amber-500", bg: "bg-amber-50", title: "IATA Accredited", sub: "International Air Transport Association" },
            { icon: "ri-verified-badge-fill", color: "text-cyan-600", bg: "bg-cyan-50", title: "PTA Registered", sub: "Pakistan Tourism Authority" },
            { icon: "ri-shield-star-fill", color: "text-green-600", bg: "bg-green-50", title: "10+ Years Serving", sub: "Established 2014 · Karachi, Pakistan" },
          ].map(acc => (
            <div key={acc.title} className="flex items-center gap-4 bg-gray-50 rounded-xl border border-gray-200 p-5">
              <div className={`w-12 h-12 flex items-center justify-center rounded-full ${acc.bg} flex-shrink-0`}>
                <i className={`${acc.icon} text-2xl ${acc.color}`}></i>
              </div>
              <div>
                <p className="text-sm font-extrabold text-gray-900">{acc.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{acc.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section className="py-4 px-4 md:px-12 mb-10">
        <div className="max-w-7xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=stunning%20aerial%20view%20of%20clouds%20and%20blue%20sky%20from%20airplane%20window%2C%20breathtaking%20sunrise%20above%20the%20clouds%2C%20warm%20golden%20orange%20tones%2C%20travel%20and%20adventure%20photography%2C%20peaceful%20and%20inspiring%20atmosphere&width=1440&height=280&seq=about-cta-bg&orientation=landscape"
              alt="Start your journey"
              className="w-full h-52 object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-700/90 to-cyan-500/80" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
              <h3 className="text-white text-xl md:text-2xl font-extrabold mb-2">Ready to Travel with Us?</h3>
              <p className="text-cyan-100 text-sm mb-5 max-w-md">
                25,000+ Pakistanis have trusted us. Now it&apos;s your turn. Call us or explore packages online.
              </p>
              <div className="flex flex-wrap items-center gap-3 justify-center">
                <a href="/tours" className="bg-white text-cyan-700 text-sm font-bold px-6 py-2.5 rounded-md hover:bg-amber-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                  View Tour Packages
                </a>
                <a href="tel:+923711432451" className="border-2 border-white text-white text-sm font-bold px-6 py-2.5 rounded-md hover:bg-white hover:text-cyan-700 transition-colors cursor-pointer whitespace-nowrap">
                  Call 03711432451
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
